/**
 * SetTableOptionsPopover — Designer-side popover wrapper for the Slice E3
 * engine-options form.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice E3.
 */
import { useState } from 'react';
import { Settings2 } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import type { QualifiedName } from '../../../../sql-grammar/model';
import {
  getSetTableOptionsUnsupportedTooltip,
  isSetTableOptionsSupported,
} from '../../edit/edits/setTableOptions';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { DisabledAffordance } from './DisabledAffordance';
import { SetTableOptionsFormBody } from './SetTableOptionsFormBody';

export interface SetTableOptionsPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly table: QualifiedName | null;
  readonly onCommitted?: () => void;
}

export function SetTableOptionsPopover(props: SetTableOptionsPopoverProps) {
  const {
    editContext,
    testId = 'set-table-options-popover',
    table,
    onCommitted,
  } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isSetTableOptionsSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getSetTableOptionsUnsupportedTooltip(engine)}
        variant="ghost"
        ariaLabel="Edit engine-specific table options"
      >
        <Settings2 className="h-3.5 w-3.5" />
        Edit…
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Edit engine-specific table options"
        >
          <Settings2 className="h-3.5 w-3.5" />
          Edit…
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="max-h-[min(480px,var(--radix-popover-content-available-height))] w-96 overflow-y-auto"
        data-testid={testId}
      >
        {open ? (
          <SetTableOptionsFormBody
            editContext={editContext}
            testId={testId}
            table={table}
            onCommitted={() => { setOpen(false); onCommitted?.(); }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
